"use client";
import {
  createContext,
  Dispatch,
  ReactElement,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ModalType, ParcelStatus, Theme } from "@/enums";
import * as Yup from "yup";
import { useTheme } from "./Theme.context";
import { parcelStepsData } from "../configs/parcelSteps";

type TOpenModalProps = {
  id?: number;
  modalType: ModalType;
};

interface IParcelsContext {
  modalOpen: boolean;
  parcelId: number | null;
  setParcelId: Dispatch<SetStateAction<number | null>>;
  parcelStatus: ParcelStatus | null;
  setParcelStatus: Dispatch<SetStateAction<ParcelStatus | null>>;
  modalType: ModalType | null;
  openParcelsModal: ({ id, modalType }: TOpenModalProps) => void;
  closeModal: () => void;
  activeStep: number;
  setActiveStep?: Dispatch<SetStateAction<number>>;
  shippingMethod: "get-parcel" | "send-parcel";
  setShippingMethod?: Dispatch<SetStateAction<"get-parcel" | "send-parcel">>;
  parcelSteps: {
    stepName: string;
    component: ReactElement<any, any>;
    validationSchema: Yup.ObjectSchema<any>;
  }[];
}

const ParcelsContext = createContext<IParcelsContext>(null);

export const useParcels = () => {
  return useContext(ParcelsContext);
};

export const ParcelsProvider = ({ children }) => {
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState(false);
  const [parcelId, setParcelId] = useState<number | null>(null);
  const [parcelStatus, setParcelStatus] = useState<ParcelStatus | null>(null);
  const [modalType, setModalType] = useState<ModalType | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [shippingMethod, setShippingMethod] = useState<
    "get-parcel" | "send-parcel"
  >("get-parcel");

  useEffect(() => {
    if (parcelId) {
      if (theme !== Theme.UZ && theme !== Theme.UK) {
        //* Note: It's extra if there's "Send Parcel" in Uzbekistan too
        activeStep === 0 && setActiveStep(1);
      }
    }
  }, [parcelId]);

  const parcelSteps = useMemo(() => {
    if (theme !== "") {
      return parcelStepsData[theme][shippingMethod];
    }
  }, [theme, shippingMethod]);

  const openParcelsModal = ({ id, modalType }: TOpenModalProps) => {
    if (id) {
      setParcelId(id);
    }
    setModalType(modalType);
    setModalOpen(true);
  };

  const resetStates = () => {
    setModalOpen(false);
    setParcelId(null);
    setParcelStatus(null);
    setModalType(null);
    setActiveStep(0);
  };

  const closeModal = () => {
    resetStates();
  };

  useEffect(() => {
    if (!modalOpen) {
      if (parcelId) {
        resetStates();
      }
    }
  }, [modalOpen]);

  return (
    <ParcelsContext.Provider
      value={{
        modalOpen,
        parcelId,
        setParcelId,
        modalType,
        openParcelsModal,
        closeModal,
        activeStep,
        setActiveStep,
        shippingMethod,
        setShippingMethod,
        parcelSteps,
        parcelStatus,
        setParcelStatus,
      }}
    >
      {children}
    </ParcelsContext.Provider>
  );
};
